import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import { CustomerMembershipStatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getMemberships, resetFilters, setFilter } from "store/slices/customersSlice";
import { CUSTOMER_MEMBERSHIP_STATUS, ORDER_BY_OPTIONS, PLAN_TYPES, STATUS_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { CurrencySymbol, formatUTCDate, getMinMaxDate } from "utils/utils";

const Membership = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    loading2,
    memberships,
    filters: { membershipNumberKeyword, dates, customerMembershipStatus, orderBy },
  } = useSelector((state) => state.Customers);

  const { noMembership } = Icons || {};
  useEffect(() => {
    getAllData();
  }, []);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 100,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      userId: id ?? "",
      keyword: isResetKeyword ? "" : membershipNumberKeyword || "",
      status: isReset ? "" : customerMembershipStatus || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };

    dispatch(getMemberships(payload));
  };
  const filtersList = [
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "customerMembershipStatus",
      placeholder: t("Status"),
      type: "singleSelect",
      options: CUSTOMER_MEMBERSHIP_STATUS,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: "Membership No.",
        key: "membershipNo",
      },
      {
        header: "Plan Name",
        key: "planId",
        render: (value) => value?.title?.en ?? t("Free Wash"),
      },
      {
        header: "Plan Type",
        key: "planId",
        render: (value) =>
          value.planType || "-",
      },
      {
        header: "License Plate",
        key: "licencePlateNumber",
      },
      {
        header: "Amount",
        key: "finalPrice",
        render: (value) => (
          <p className="price_symbol">
            <CurrencySymbol width="12" height="14" />
            {value}
          </p>
        ),
      },
      {
        header: "Usage",
        key: "usage",
        render: (value) => value?.totalUses,
      },
      {
        header: "Status",
        key: "status",
        render: (value) => (
          <CustomerMembershipStatusBadge status={value} t={t} />
        ),
      },
      {
        header: "Billing Cycle Start",
        key: "billingCycleStart",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: "Billing Cycle End",
        key: "billingCycleEnd",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: "Created Date",
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
    ],
    [t],
  );
  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  return (
    <div className="p-4">
      <FilterBar
        keyword={membershipNumberKeyword}
        setKeyword={(value) =>
          dispatch(setFilter({ key: "membershipNumberKeyword", value }))
        }
        keywordPlaceholder={t("Search by membership no or license plate number")}
        searchMaxWidth={400}
        filters={filtersList}
        selectedFilters={{ membershipNumberKeyword, customerMembershipStatus, orderBy }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
      />
      {loading2 && <TableShimmer rows={12} columns={8} />}
      {!loading2 && memberships?.length === 0 && (
        <NoDataFound title={t("No memberships found")} icon={noMembership} />
      )}
      {!loading2 && memberships?.length > 0 && (
        <>
          <DataTable columns={columns} data={memberships} t={t} />
        </>
      )}
    </div>
  );
};

export default Membership;
